<template>
  <div class="import-users-csv-modal">
    <div v-if="csvLoading" class="info-section d-flex w-100 flex-column justify-space-between">
      <div class="csv-upload-report d-flex w-100 pl-4 pt-4 pr-4 pb-4">
        <v-icon class="file-icon mr-2" size="70" icon="$dataNoHexagon" />
        <div class="d-flex flex-column align-start justify-center w-100">
          <div class="subtitle2">{{ file?.name }}</div>
          <div class="body3 text-indigo-medium mb-2">{{ humanizeFileSize(file?.size) }}</div>
          <v-progress-linear
            color="green-base"
            bg-color="indigo-pale"
            rounded
            height="4"
            indeterminate
          />
        </div>
      </div>
      <v-btn
        rounded
        class="close-btn align-self-end"
        color="primary"
        data-testid="modal-cancel-button"
        size="large"
        @click="$emit('dismissed')"
      >
        {{ $t("general.cancel") }}
      </v-btn>
    </div>
    <template v-else>
      <v-form ref="form" v-model="valid" validate-on="input lazy" @submit.prevent>
        <draggable-file-uploader
          :description="$t(`modals.${config.action}.uploadTxt`)"
          :subtitle="
            $t(`modals.${config.action}.subtitle`, {
              size: config.item.maxFileSize ? humanizeFileSize(config.item.maxFileSize) : '1 MB',
            })
          "
          :class="{ 'error-border': !!errorMessage }"
          :error-message="errorMessage"
          @file-changed="handleFileUpload($event)"
        />
        <div class="body3 mt-2">
          <a data-testid="download-template-link" class="coro-link" @click="downloadTemplate()">{{
            $t(`modals.${config.action}.downloadTemplate`)
          }}</a>
          {{ $t(`modals.${config.action}.disclaimer`) }}
        </div>
        <div class="d-flex justify-end mt-5 mb-3">
          <v-btn
            data-testid="modal-cancel-button"
            color="primary"
            :ripple="false"
            variant="text"
            @click="$emit('dismissed')"
          >
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn
            rounded
            color="primary"
            data-testid="modal-action-button"
            size="large"
            :disabled="disable"
            @click="handleFileImport()"
          >
            {{ $t(`modals.${config.action}.actionBtn`) }}
          </v-btn>
        </div>
      </v-form>
    </template>
  </div>
</template>

<script lang="ts">
import DraggableFileUploader from "@/components/DraggableFileUploader.vue";
import { computed, defineComponent, type PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useImportCsvModal } from "@/composables/useImportCsvModal";
import { DataSizeUnit } from "@/constants/general";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { humanizeFileSize } from "@coronet/cui";
import { useSnackbarStore, SnackbarTypes } from "@/_store/snackbar.module";

export default defineComponent({
  components: {
    DraggableFileUploader,
  },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig>,
      required: true,
    },
    disable: {
      type: Boolean,
      required: false,
    },
  },
  emits: ["dismissed"],
  setup(props, { emit }) {
    const valid = ref(true);
    const form = ref();
    const isFormSubmitted = ref(false);

    const i18n = useI18n();
    const snackbarStore = useSnackbarStore();

    const {
      file,
      csvLoading,
      csvLoadError,
      errorMessage: csvErrorMessage,
      handleFileUpload,
      handleImport,
    } = useImportCsvModal();

    const errorMessage = computed(() => {
      if (file.value && file.value.size > (props.config.item.maxFileSize || DataSizeUnit.MB)) {
        return i18n.t("validations.fileSizeError");
      }
      if (isFormSubmitted.value && !file.value) {
        return i18n.t("validations.missingFile");
      }
      return "";
    });

    const handleFileImport = async () => {
      isFormSubmitted.value = true;
      if (!file.value || !!errorMessage.value) {
        return;
      }

      await handleImport(file.value, props.config.item.uploadCallback);

      if (csvLoadError.value) {
        const message =
          csvErrorMessage.value || i18n.t(`modals.${props.config.action}.errors.general`);
        snackbarStore.add({ html: message, type: SnackbarTypes.ERROR });
        csvLoadError.value = false;
        csvErrorMessage.value = undefined;
        return;
      }

      await props.config.item.successCallback();
      emit("dismissed");
    };

    const downloadTemplate = async () => {
      await props.config.item.downloadTemplateCallback();
    };

    return {
      form,
      valid,
      file,
      csvLoading,
      errorMessage,
      handleFileUpload,
      handleFileImport,
      downloadTemplate,
      humanizeFileSize,
    };
  },
});
</script>

<style lang="scss" scoped>
.import-users-csv-modal,
.info-section {
  min-height: 300px;
}

.error-border {
  border: dashed 1px rgb(var(--v-theme-red-dark));
}

.csv-upload-report {
  border: solid 1px rgb(var(--v-theme-indigo-pale));
  border-radius: 8px;
}

.file-icon {
  background-color: rgb(var(--v-theme-indigo-faint));
  border-radius: 8px;
}

.close-btn {
  margin-bottom: 24px;
}
</style>
